import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

/**
 * NOTE on token storage:
 * Tokens live in localStorage so that the SPA can attach them to API requests
 * via the axios interceptor. The trade-off is XSS exposure. The proper hardened
 * approach is httpOnly+SameSite cookies issued by the backend, which requires
 * backend cookie-setting + CSRF protection. That's intentionally deferred for
 * this MVP; the app does not load third-party scripts, which limits the risk.
 */

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("fawateery_token");
    const stored = localStorage.getItem("fawateery_user");
    if (token && stored) {
      try {
        setUser(JSON.parse(stored));
      } catch (e) {
        console.warn("Failed to parse stored user, clearing session", e);
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("fawateery_token", res.data.access_token);
    localStorage.setItem("fawateery_user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const refreshUser = useCallback(async () => {
    const res = await api.get("/auth/me");
    localStorage.setItem("fawateery_user", JSON.stringify(res.data));
    setUser(res.data);
    return res.data;
  }, []);

  const register = useCallback(async (email, password, name, extras = {}) => {
    const res = await api.post("/auth/register", { email, password, name, ...extras });
    // Pending users get { registered:true, status:"pending", message }
    // Admin/first-account gets { access_token, user } and is logged in immediately.
    if (res.data?.access_token) {
      localStorage.setItem("fawateery_token", res.data.access_token);
      localStorage.setItem("fawateery_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      return { pending: false, user: res.data.user };
    }
    return { pending: true, message: res.data?.message || "تم إنشاء الحساب. حسابك بانتظار موافقة مسؤول النظام." };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("fawateery_token");
    localStorage.removeItem("fawateery_user");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refreshUser, isAdmin: user?.role === "admin" }),
    [user, loading, login, register, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
